import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';

export const labelsFor = (remoteName) => ({
  create_users: 'New members added to the app', update_users: 'Members updated in the app',
  append_rows: `New rows added to ${remoteName}`, create_remote: `New contacts created in ${remoteName}`,
  update_rows: `${remoteName} rows updated`, update_remote: `${remoteName} contacts updated`,
  conflicts: 'Conflicts (last modified wins)',
  removed_from_sheet: `Missing from ${remoteName} (kept in app)`, removed_from_remote: `Missing from ${remoteName} (kept in app)`,
  removed_from_app: `Removed from app (left in ${remoteName})`,
});

export function SyncChangesTable({ changes, remoteName, testid = 'sync-changes' }) {
  const [q, setQ] = useState('');
  const rows = (changes || []).filter((c) => !q || c.label.toLowerCase().includes(q.toLowerCase()) || c.changes.some((x) => `${x.field} ${x.from} ${x.to}`.toLowerCase().includes(q.toLowerCase())));
  if (!changes || changes.length === 0) return <p className="text-xs mt-2" style={{ color: 'var(--olive)' }} data-testid={`${testid}-empty`}>No field changes recorded for this run.</p>;
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="relative max-w-xs mb-2">
        <Search size={13} className="absolute left-2 top-2.5" style={{ color: 'var(--olive)' }} />
        <Input data-testid={`${testid}-filter`} className="pl-7 h-8 text-xs" placeholder="Filter by member or value…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto rounded border" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
        <table className="text-xs w-full border-collapse">
          <thead className="sticky top-0"><tr>{['Member', 'Where', 'Why', 'Field', 'Before', 'After'].map((h) => <th key={h} className="px-2 py-1 text-left" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c, i) => (c.changes.length ? c.changes : [{ field: '—', from: '', to: '' }]).map((x, j) => (
              <tr key={`${i}-${j}`} style={{ background: i % 2 ? 'rgba(0,0,0,0.02)' : 'white' }} data-testid={`${testid}-row`}>
                {j === 0 && <td className="px-2 py-1 font-title align-top" rowSpan={c.changes.length || 1}>{c.label}</td>}
                {j === 0 && <td className="px-2 py-1 align-top" rowSpan={c.changes.length || 1}>{c.side === 'app' ? 'App' : remoteName} · {c.action}</td>}
                {j === 0 && <td className="px-2 py-1 align-top" rowSpan={c.changes.length || 1} style={{ color: c.reason.startsWith('conflict') ? '#8a5a00' : 'var(--olive)' }}>{c.reason.replace('remote', remoteName)}</td>}
                <td className="px-2 py-1">{x.field}</td>
                <td className="px-2 py-1" style={{ color: '#8a1f11' }}>{x.from || <i style={{ opacity: 0.5 }}>blank</i>}</td>
                <td className="px-2 py-1" style={{ color: '#1a7f37' }}>{x.to || <i style={{ opacity: 0.5 }}>blank</i>}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] mt-1" style={{ color: 'var(--olive)' }}>{rows.length} of {changes.length} members shown.</p>
    </div>
  );
}

export function SyncResultPanel({ res, remoteName, testid }) {
  if (!res) return null;
  if (res.error) return <p className="mt-3 text-sm" data-testid={testid} style={{ color: '#8a1f11' }}>{res.error}</p>;
  if (res.mode === 'pull') {
    return <p className="mt-3 text-sm" data-testid={testid} style={{ color: '#1a7f37' }}>Imported {res.created} new, updated {res.updated}, skipped {res.skipped}.</p>;
  }
  const labels = labelsFor(remoteName);
  const s = res.summary || {}; const d = res.details || {};
  const keys = Object.keys(labels).filter((k) => s[k] > 0);
  return (
    <div className="mt-3 text-sm space-y-2" data-testid={testid}>
      <p style={{ color: res.dry_run ? 'var(--navy)' : '#1a7f37' }}>
        {res.dry_run ? 'Preview — nothing has been changed yet.' : 'Sync complete.'} {s.unchanged ?? 0} record{s.unchanged === 1 ? '' : 's'} already in sync.
        {res.google_total != null && ` ${res.google_total} contacts in Google.`}
      </p>
      {res.errors?.length > 0 && <p style={{ color: '#8a1f11' }} data-testid={`${testid}-errors`}>{res.errors.length} item(s) failed: {res.errors.slice(0, 3).join(' · ')}</p>}
      <div className="grid sm:grid-cols-2 gap-2">
        {keys.map((k) => (
          <details key={k} className="rounded border px-3 py-2" style={{ borderColor: 'rgba(0,0,0,0.1)', background: k === 'conflicts' ? '#fff8e6' : 'white' }} data-testid={`sync-${k}`}>
            <summary className="cursor-pointer font-title">{labels[k]}: <b>{s[k]}</b></summary>
            <ul className="mt-1 text-xs list-disc pl-4" style={{ color: 'var(--olive)' }}>
              {(d[k] || []).map((x, i) => <li key={i}>{typeof x === 'string' ? x : `${x.label} → ${x.winner === 'app' ? 'app version kept' : `${remoteName} version kept`}`}</li>)}
              {s[k] > (d[k] || []).length && <li>…and {s[k] - d[k].length} more</li>}
            </ul>
          </details>
        ))}
      </div>
      {keys.length === 0 && <p style={{ color: 'var(--olive)' }}>Everything is already in sync.</p>}
      {res.changes && keys.length > 0 && (
        <details data-testid={`${testid}-changes`}>
          <summary className="cursor-pointer text-xs font-title" style={{ color: 'var(--navy)' }}>Field-by-field changes ({res.changes.length} members)</summary>
          <SyncChangesTable changes={res.changes} remoteName={remoteName} testid={`${testid}-changes-table`} />
        </details>
      )}
    </div>
  );
}
